import { ComponentProps, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { LongArrow } from '@/components/brand/long-arrow';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

// Content column of the auth entry points (Login / Create Account), in the
// brand book's style: blue eyebrow, large Jakarta title, 52px fields. The
// two-column frame around it (logo, live 3D backdrop) lives in
// AuthRouteTransition so the backdrop stays mounted when switching between
// the two routes instead of re-creating its WebGL scene on every swap.

/** Field style shared by every auth form input. */
export const AUTH_INPUT_CLASS =
  'h-[52px] rounded-xl border-[#DDE1E7] bg-white px-4 text-base text-[#15191E] placeholder:text-[#6B7280] focus-visible:border-[#155DFC] focus-visible:ring-4 focus-visible:ring-[#155DFC]/15 focus-visible:ring-offset-0 md:text-base';

export interface AuthPageLayoutProps {
  children: ReactNode;
  className?: string;
}

export function AuthPageLayout({ children, className }: AuthPageLayoutProps) {
  return (
    <div className={cn('flex w-full flex-col', className)}>{children}</div>
  );
}

export interface AuthPageLayoutHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}

function AuthPageLayoutHeader({
  title,
  subtitle,
  className,
}: AuthPageLayoutHeaderProps) {
  const { LL } = useI18nContext();

  return (
    <div className={cn('mb-9 flex flex-col gap-[18px]', className)}>
      <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#155DFC]">
        <span>{LL.auth.eyebrow()}</span>
        <span aria-hidden="true" className="h-px w-12 bg-[#155DFC]/35" />
      </div>
      <h1 className="text-[44px] font-medium leading-none tracking-[-0.04em] text-[#15191E] lg:text-[56px]">
        {title}
      </h1>
      {subtitle && (
        <p className="text-balance text-[17px] leading-relaxed text-[#4B5361]">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export interface AuthPageLayoutContentProps {
  children: ReactNode;
  className?: string;
}

function AuthPageLayoutContent({
  children,
  className,
}: AuthPageLayoutContentProps) {
  return <div className={cn('flex flex-col gap-4', className)}>{children}</div>;
}

export interface AuthPageLayoutFooterProps {
  children: ReactNode;
  className?: string;
}

function AuthPageLayoutFooter({
  children,
  className,
}: AuthPageLayoutFooterProps) {
  return <div className={cn('mt-9 flex', className)}>{children}</div>;
}

const PRIMARY_CLASS =
  'group h-[52px] w-full gap-3.5 rounded-xl bg-[#155DFC] text-[15px] font-semibold text-white hover:bg-[#0B4AE0] focus-visible:ring-[#155DFC] focus-visible:ring-offset-[#F9F9F9]';

/** Primary submit with the brand book's long arrow, which stretches on hover. */
function AuthPageLayoutSubmit({
  children,
  className,
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button type="submit" className={cn(PRIMARY_CLASS, className)} {...props}>
      {children}
      <LongArrow lineClassName="h-[1.5px] w-6 group-enabled:group-hover:w-10" />
    </Button>
  );
}

/** The primary look for a link (e.g. "Abrir o Gmail"). */
function AuthPageLayoutPrimaryLink({
  children,
  className,
  ...props
}: ComponentProps<'a'>) {
  return (
    <Button asChild className={cn(PRIMARY_CLASS, className)}>
      <a {...props}>
        <span>{children}</span>
        <LongArrow lineClassName="h-[1.5px] w-6 group-hover:w-10" />
      </a>
    </Button>
  );
}

/** Outlined companion of the primary action, same 52px height. */
function AuthPageLayoutSecondary({
  className,
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'h-[52px] w-full gap-2.5 rounded-xl border-[#DDE1E7] bg-white text-[15px] font-semibold tabular-nums text-[#15191E] hover:border-[#155DFC] hover:bg-white focus-visible:ring-[#155DFC] focus-visible:ring-offset-[#F9F9F9] disabled:text-[#5B6371] disabled:opacity-100',
        className
      )}
      {...props}
    />
  );
}

AuthPageLayout.Header = AuthPageLayoutHeader;
AuthPageLayout.Content = AuthPageLayoutContent;
AuthPageLayout.Footer = AuthPageLayoutFooter;
AuthPageLayout.Submit = AuthPageLayoutSubmit;
AuthPageLayout.PrimaryLink = AuthPageLayoutPrimaryLink;
AuthPageLayout.Secondary = AuthPageLayoutSecondary;
